import { useState, useCallback } from 'react';
import * as L from 'leaflet';
import { DropPoint, PlaceContext } from '@m4a3/api-client';
import { orderfulApi } from '../../../services/api-instances';
import { normalizeDropPoints } from '../../../utils/cellDropPointUtils';
import { RiskAssessment, RiskUtils } from '../utils/riskUtils';
import { logToDatadog } from '../../../services/datadog';
import { StatusType } from '@datadog/browser-logs';
import { MAP_CONFIG } from '../constants/mapConfig';

interface DropPointState {
  dropPoints: DropPoint[];
  newPinLocation: L.LatLng | null;
  riskAssessment: RiskAssessment | null;
  isLoadingRiskAssessment: boolean;
  isDraggingPin: boolean;
}

export const useDropPointOperations = () => {
  const [dropPoints, setDropPoints] = useState<DropPoint[]>([]);
  const [newPinLocation, setNewPinLocation] = useState<L.LatLng | null>(null);
  const [riskAssessment, setRiskAssessment] = useState<RiskAssessment | null>(null);
  const [isLoadingRiskAssessment, setIsLoadingRiskAssessment] = useState(false);
  const [isDraggingPin, setIsDraggingPin] = useState(false);

  // Perform comprehensive risk assessment for a location
  const performRiskAssessment = useCallback(async (
    latitude: number,
    longitude: number,
    cellId?: number
  ): Promise<RiskAssessment> => {
    setIsLoadingRiskAssessment(true);
    
    try {
      // Single API call that provides both building and height data
      const riskResponse = await orderfulApi.evaluateDropPointRisk({
        latitude,
        longitude,
        radius: MAP_CONFIG.RISK_ASSESSMENT_RADIUS ?? 3.0,
        cellId: (cellId ?? MAP_CONFIG.DEFAULT_CELL_ID) ?? 7,
      });
      
      // Extract height data from the new API response format
      const heightData = riskResponse.heightData?.height || null;
      
      // Convert boolean flags to our expected format
      const buildingRiskData = {
        risk: riskResponse.isNearBuilding ? 'HIGH' : 'LOW' as 'HIGH' | 'LOW',
        reason: riskResponse.isNearBuilding ? 'Near building detected' : 'Clear of buildings'
      };
      
      const assessment = RiskUtils.getComprehensiveRiskAssessment(
        heightData,
        buildingRiskData,
        cellId
      );
      
      return assessment;
    } catch (error) {
      console.error('Risk assessment failed:', error);
      logToDatadog('risk_assessment_failed', { latitude, longitude, cellId, error }, StatusType.error);
      
      // Return unknown risk assessment
      return RiskUtils.getComprehensiveRiskAssessment(null, null, cellId);
    } finally {
      setIsLoadingRiskAssessment(false);
    }
  }, []);

  // Handle pin movement (drag or click) during pin placement
  const handlePinMove = useCallback(async (newPosition: L.LatLng) => {
    setNewPinLocation(newPosition);
    
    // Perform risk assessment for the new position
    if (!isDraggingPin) {
      setIsDraggingPin(true);
    }
    
    const assessment = await performRiskAssessment(newPosition.lat, newPosition.lng, MAP_CONFIG.DEFAULT_CELL_ID);
    setRiskAssessment(assessment);
    setIsDraggingPin(false);
  }, [performRiskAssessment, isDraggingPin]);

  // Create a new drop point
  const createNewDropPoint = useCallback(async (
    placeContext: PlaceContext,
    coordinates: L.LatLng,
    assessment: RiskAssessment
  ): Promise<DropPoint> => {
    if (!assessment.canSave) {
      throw new Error('Cannot save drop point due to risk assessment');
    }

    const newDropPoint = await orderfulApi.createDropPoint(
      placeContext.placeId,
      {
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        verified: false,
      }
    );

    logToDatadog('drop_point_created_success', {
      placeId: placeContext.placeId,
      coordinates: { lat: coordinates.lat, lng: coordinates.lng },
      riskAssessment: assessment
    });

    return newDropPoint;
  }, []);

  // Update an existing drop point
  const updateExistingDropPoint = useCallback(async (
    locationId: string,
    dropPointId: string, 
    data: { risk: number; verified: boolean }
  ): Promise<DropPoint> => {
    const updatedDropPoint = await orderfulApi.updateDropPoint(locationId, dropPointId, data); //TODO-Alan: Clarify with Alan if this is correct 
    
    logToDatadog('drop_point_updated', { locationId, dropPointId, data });
    return updatedDropPoint;
  }, []);

  // Delete a drop point
  const deleteExistingDropPoint = useCallback(async (dropPoint: DropPoint) => {
    await orderfulApi.deleteDropPoint(dropPoint.id);
    
    logToDatadog('drop_point_delete_success', {
      dropPointId: dropPoint.id,
      coordinates: { lat: dropPoint.latitude, lng: dropPoint.longitude }
    });
  }, []);

  // Evaluate risk for an existing drop point
  const evaluateDropPoint = useCallback(async (dropPoint: DropPoint) => {
    try {
      const assessment = await performRiskAssessment(
        dropPoint.latitude, 
        dropPoint.longitude, 
        MAP_CONFIG.DEFAULT_CELL_ID
      );
      setRiskAssessment(assessment);
      
      logToDatadog('drop_point_evaluated', {
        dropPointId: dropPoint.id,
        coordinates: { lat: dropPoint.latitude, lng: dropPoint.longitude },
        riskAssessment: assessment
      });
    } catch (error) {
      console.error('Error evaluating drop point:', error);
      logToDatadog('drop_point_evaluation_failed', { 
        dropPointId: dropPoint.id, 
        error 
      }, StatusType.error);
      throw error;
    }
  }, [performRiskAssessment]);

  // Update drop points from place context
  const updateDropPointsFromContext = useCallback((placeContext: PlaceContext) => {
    const dropPointsArray = normalizeDropPoints(placeContext.dropPoints);
    setDropPoints(dropPointsArray);
  }, []);

  // Start adding a new pin
  const startAddingPin = useCallback(async (
    placeContext: PlaceContext,
    mapCenter: [number, number]
  ) => {
    // Clear any current risk assessment
    setRiskAssessment(null);
    
    // Get place centroid coordinates from the place geometry or use map center as fallback
    let latitude, longitude;
    
    try {
      // Try to parse place geometry to get centroid
      const geometry = typeof placeContext.place.geometry === 'string' 
        ? JSON.parse(placeContext.place.geometry)
        : placeContext.place.geometry;
        
      if (geometry.type === 'Polygon' && geometry.coordinates && geometry.coordinates[0]) {
        const coords = geometry.coordinates[0];
        longitude = coords.reduce((sum: number, coord: number[]) => sum + coord[0], 0) / coords.length;
        latitude = coords.reduce((sum: number, coord: number[]) => sum + coord[1], 0) / coords.length;
      } else {
        throw new Error('Cannot extract coordinates from geometry');
      }
    } catch (error) {
      console.warn('Failed to get place centroid, using map center:', error);
      latitude = mapCenter[0];
      longitude = mapCenter[1];
    }
    
    // Place pin at calculated location
    const pinLocation = L.latLng(latitude, longitude);
    setNewPinLocation(pinLocation);
    
    // Start risk assessment for the initial pin location
    const assessment = await performRiskAssessment(latitude, longitude, MAP_CONFIG.DEFAULT_CELL_ID);
    setRiskAssessment(assessment);
    
    logToDatadog('pin_add_mode_started', { 
      placeId: placeContext.placeId,
      pinLocation: { latitude, longitude }
    });
  }, [performRiskAssessment]);

  // Cancel pin placement
  const cancelPinPlacement = useCallback(() => {
    setNewPinLocation(null);
    setRiskAssessment(null);
  }, []);

  // Clear risk assessment (useful when switching places)
  const clearRiskAssessment = useCallback(() => {
    setRiskAssessment(null);
  }, []);

  return {
    // State
    dropPoints,
    newPinLocation,
    riskAssessment,
    isLoadingRiskAssessment,
    isDraggingPin,

    // Actions
    performRiskAssessment,
    handlePinMove,
    createNewDropPoint,
    updateExistingDropPoint,
    deleteExistingDropPoint,
    evaluateDropPoint,
    updateDropPointsFromContext,
    startAddingPin,
    cancelPinPlacement,
    clearRiskAssessment,

    // Direct setters
    setDropPoints,
    setNewPinLocation,
    setRiskAssessment,
  };
};
